import { Trash2 } from "lucide-react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { AssessmentSummary } from "@/lib/api";
import { cn } from "@/lib/utils";

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) {
    return "-";
  }
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

interface AssessmentTableProps {
  rows: AssessmentSummary[];
  /** Text of the per-row action link (e.g. "Review →"). */
  linkLabel: string;
  /** Builds the per-row action link destination. */
  linkTo: (assessmentId: string) => string;
  /**
   * When provided, renders a trailing per-row delete button. Omitted by the
   * expert queue, which never deletes; supplied by the owner's history.
   */
  onDelete?: (row: AssessmentSummary) => void;
  /**
   * When true, renders a "Client" column (owner name + email) after Company.
   * Only the expert queue has these fields; the owner's history leaves it off.
   */
  showClient?: boolean;
}

/** Summary table shared by the expert review queue and the user history page. */
export function AssessmentTable({
  rows,
  linkLabel,
  linkTo,
  onDelete,
  showClient,
}: AssessmentTableProps) {
  const headings = ["Company", "Sector", "Position", "Exposure", "Status", "Created", ""];
  if (showClient) {
    headings.splice(1, 0, "Client");
  }
  if (onDelete) {
    headings.push("");
  }

  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-border border-b">
            {headings.map((heading, i) => (
              <th
                key={`${heading}-${i}`}
                className="text-muted px-4 py-3 text-left text-[11px] font-semibold tracking-[0.08em] uppercase"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.assessment_id} className="border-border/60 border-b last:border-b-0">
              <td className="text-primary px-4 py-3 font-bold">{row.company_name}</td>
              {showClient && <ClientCell row={row} />}
              <td className="text-muted px-4 py-3">{row.sector ?? "-"}</td>
              <td className="px-4 py-3">{row.strategic_position ?? "-"}</td>
              <td className="px-4 py-3">{fmt(row.overall_exposure_score)}</td>
              <td className="px-4 py-3">
                <span
                  className={cn(
                    "rounded px-2 py-0.5 text-[11px] font-bold tracking-wide uppercase",
                    row.reviewed_at
                      ? "bg-accent text-accent-foreground"
                      : "bg-accent-light/60 text-accent",
                  )}
                >
                  {row.reviewed_at ? "reviewed" : row.status}
                </span>
              </td>
              <td className="text-muted px-4 py-3">
                {new Date(row.created_at).toLocaleDateString()}
              </td>
              <td className="px-4 py-3">
                <Link
                  to={linkTo(row.assessment_id)}
                  className="text-accent font-medium hover:underline"
                >
                  {linkLabel}
                </Link>
              </td>
              {onDelete && (
                <td className="px-4 py-3 text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(row)}
                    aria-label={`Delete assessment for ${row.company_name}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/** Renders the client's name and a mailto link, or a muted placeholder. */
function ClientCell({ row }: { row: AssessmentSummary }) {
  const { owner_name, owner_email } = row;

  if (!owner_name && !owner_email) {
    return <td className="text-muted px-4 py-3">No client on file</td>;
  }

  return (
    <td className="px-4 py-3">
      {owner_name && <div className="text-primary font-medium">{owner_name}</div>}
      {owner_email && (
        <a href={`mailto:${owner_email}`} className="text-accent hover:underline">
          {owner_email}
        </a>
      )}
    </td>
  );
}
